import {
  FIELD_LABELS,
  leadPushBlockers,
  leadTasks,
  type LeadBlocker,
  type SyncOp,
} from '@wr/shared';
import { ApiError, get, post } from '../../api.js';
import { contactPushBlockers } from '../../lists.js';
import type { LocalRecord, LocalStore } from '../../offline/store.js';
import { fullName, isUnreachable } from '../people/data.js';

/** A contact of a lead, as the lead sheet shows it. */
export type LeadContact = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  company: string | null;
  email: string | null;
  mobile: string | null;
  office_phone: string | null;
  created_by?: string | null;
  /** A scanned card is kept for this contact. */
  has_card?: boolean;
  is_primary: boolean;
  position: number;
};

export interface LeadRoi {
  id: string;
  scenarioName: string;
  calculator: string;
  payback: number | null;
  roiYear1: number | null;
}

export interface LeadSheetData {
  lead: LocalRecord;
  contacts: LeadContact[];
  createdByName: string | null;
  ownerName: string | null;
  eventCode: string | null;
  /** Null when ROI is off or not allowed. */
  roi: LeadRoi[] | null;
  /** The deal's page in HubSpot, once sent. */
  hubspotUrl?: string | null;
  /** True when it comes from this device only (no network). */
  offline?: boolean;
}

const str = (v: unknown) => (typeof v === 'string' && v ? v : null);

function asContact(
  c: LocalRecord,
  link: LocalRecord | { is_primary: boolean; position: number },
): LeadContact {
  return {
    id: c.id,
    first_name: str(c.first_name),
    last_name: str(c.last_name),
    job_title: str(c.job_title),
    company: str(c.company),
    email: str(c.email),
    mobile: str(c.mobile),
    office_phone: str(c.office_phone),
    created_by: str(c.created_by),
    has_card: Boolean(c.card_image_key),
    is_primary: Boolean(link.is_primary),
    position: Number(link.position ?? 0),
  };
}

/** A change waiting on this device that touches the lead. */
function touches(op: SyncOp, id: string): boolean {
  return op.entity === 'lead_contact' ? op.leadId === id : op.entity === 'lead' && op.id === id;
}

/**
 * The lead sheet: from the portal, with what this device changed and has
 * not sent yet; without network (or for a lead not sent yet), what this
 * device keeps.
 */
export async function loadLead(
  id: string,
  store: LocalStore | null,
  me: { id: string; name: string },
): Promise<LeadSheetData | null> {
  let remote: LeadSheetData | null = null;
  try {
    remote = await get<LeadSheetData>(`/leads/${encodeURIComponent(id)}`);
  } catch (error) {
    const missing = error instanceof ApiError && error.status === 404;
    if (!store || !(missing || isUnreachable(error))) throw error;
  }
  if (!store) return remote;
  const waiting = (await store.outbox()).map((i) => i.op).filter((op) => touches(op, id));
  if (remote && waiting.length === 0) return remote;

  const local = await store.getRecord('lead', id);
  if (!local || local.deleted) return remote;
  const lead = { ...(remote?.lead ?? {}), ...local };
  const contacts = await contactsOf(id, store, remote, waiting);
  const events = await store.listRecords('event');
  const mine = (userId: unknown) => !userId || userId === me.id;
  return {
    lead,
    contacts,
    createdByName: remote?.createdByName ?? (mine(lead.created_by) ? me.name : null),
    ownerName: remote?.ownerName ?? (mine(lead.owner_user_id) ? me.name : null),
    eventCode:
      (events.find((e) => e.id === lead.event_id)?.event_code as string | undefined) ??
      remote?.eventCode ??
      null,
    roi: remote?.roi ?? null,
    offline: !remote,
  };
}

/** The portal's list of contacts with this device's changes, or this device's own. */
async function contactsOf(
  id: string,
  store: LocalStore,
  remote: LeadSheetData | null,
  waiting: SyncOp[],
): Promise<LeadContact[]> {
  const find = async (contactId: string, link: { is_primary: boolean; position: number }) => {
    const kept = await store.getRecord('contact', contactId);
    const known = remote?.contacts.find((c) => c.id === contactId);
    if (kept && !kept.deleted) return { ...(known ?? {}), ...asContact(kept, link) };
    return known ? { ...known, ...link } : asContact({ id: contactId }, link);
  };
  let list: LeadContact[];
  if (remote) {
    list = remote.contacts.map((c) => ({ ...c }));
    for (const op of waiting) {
      if (op.entity !== 'lead_contact') continue;
      const at = list.findIndex((c) => c.id === op.contactId);
      if (op.action === 'unlink') {
        const [gone] = at >= 0 ? list.splice(at, 1) : [];
        if (gone?.is_primary && list[0]) list[0].is_primary = true;
        continue;
      }
      const primary = op.isPrimary ?? (at >= 0 ? list[at]!.is_primary : list.length === 0);
      if (primary) list.forEach((c) => (c.is_primary = false));
      const link = { is_primary: primary, position: op.position ?? list.length };
      if (at >= 0) list[at] = { ...list[at]!, ...link };
      else list.push(await find(op.contactId, link));
    }
  } else {
    const links = (await store.listRecords('lead_contact')).filter((l) => l.lead_id === id);
    list = await Promise.all(
      links.map((l) =>
        find(String(l.contact_id), {
          is_primary: Boolean(l.is_primary),
          position: Number(l.position ?? 0),
        }),
      ),
    );
  }
  return list.sort(
    (a, b) => Number(b.is_primary) - Number(a.is_primary) || a.position - b.position,
  );
}

export interface Owner {
  id: string;
  name: string;
}

/**
 * The deal owners the form offers: the portal's users who can hold leads.
 * Without network, the signed-in user (the default) and the current owner.
 */
export async function loadOwners(): Promise<Owner[] | null> {
  try {
    return (await get<{ items: Owner[] }>('/leads/owners')).items;
  } catch (error) {
    if (isUnreachable(error)) return null;
    throw error;
  }
}

/** One record of To do (brief section 6.9): what blocks its push, and its tasks. */
export interface TodoItem {
  kind: 'contact' | 'lead';
  id: string;
  title: string;
  sub: string;
  missing: { field: string; label: string }[];
  tasks: string[];
}

/**
 * What blocks the signed-in user's own records (brief section 6.9): contacts
 * to correct (a missing required field, a scan field not checked) and leads
 * to document. Built from this device, so it works offline; the device keeps
 * every record its user created or owns.
 */
export async function todoItems(store: LocalStore, userId: string): Promise<TodoItem[]> {
  const [contacts, leads, links, events] = await Promise.all([
    store.listRecords('contact'),
    store.listRecords('lead'),
    store.listRecords('lead_contact'),
    store.listRecords('event'),
  ]);
  const eventCode = (id: unknown) =>
    (events.find((e) => e.id === id)?.event_code as string | undefined) ?? null;
  const mine = (r: LocalRecord) => !r.created_by || r.created_by === userId;
  const items: TodoItem[] = [];
  for (const l of leads) {
    if (l.deleted || l.hubspot_status === 'synced') continue;
    if (!(mine(l) || l.owner_user_id === userId)) continue;
    const missing: LeadBlocker[] = leadPushBlockers(l);
    if (missing.length === 0) continue;
    const primary = links
      .filter((x) => x.lead_id === l.id)
      .sort((a, b) => Number(b.is_primary) - Number(a.is_primary))
      .map((x) => contacts.find((c) => c.id === x.contact_id))[0];
    items.push({
      kind: 'lead',
      id: l.id,
      title: str(l.company_name) || (primary ? fullName(primary) : '') || 'Lead',
      sub: ['Lead', eventCode(l.event_id)].filter(Boolean).join(', '),
      missing,
      tasks: leadTasks(l),
    });
  }
  for (const c of contacts) {
    if (c.deleted || c.hubspot_status === 'synced' || !mine(c)) continue;
    // A card being read completes itself first (brief section 8).
    if (c.reading_status === 'waiting' || c.reading_status === 'reading') continue;
    const missing = contactPushBlockers(c).map((b) => ({
      field: b.field,
      label: b.code === 'unverified' ? `Check ${FIELD_LABELS[b.field]}` : FIELD_LABELS[b.field],
    }));
    if (missing.length === 0) continue;
    items.push({
      kind: 'contact',
      id: c.id,
      title: fullName(c) || str(c.company) || str(c.email) || 'Contact',
      sub: [str(c.company), eventCode(c.event_id)].filter(Boolean).join(', ') || 'Contact',
      missing,
      tasks: [],
    });
  }
  return items;
}

/**
 * A HubSpot contact chosen in the portal: imported as already synced, never
 * duplicated (brief section 6.7). Its portal ID; needs the network.
 */
export async function importHubSpotContact(hubspotId: string): Promise<string> {
  return (await post<{ id: string }>('/people/import', { hubspotId })).id;
}

/** A deal stage: HubSpot's (step 4), or the portal's provisional list. */
export interface Stage {
  id: string;
  label: string;
  /** The stage a lead takes when qualified or not (HUBSPOT_STAGE_QUALIFIED, HUBSPOT_STAGE_DISQUALIFIED). */
  default?: 'qualified' | 'disqualified';
}
